import { useEffect, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useWindowSize } from '@react-hook/window-size'
import { useDispatch, useSelector } from 'react-redux'

import { getOrderBy } from '@/features/sop-tabs'
import { useStandardOperatingProceduresQuery } from '@/graphql/generated/hooks'
import { SopType } from '@/graphql/generated/schemas'
import {
  closeSOPIncidentDrawer,
  selectSOPIncidentDrawerState,
  selectSOPPageSize,
} from '@/redux/ui/uiSlice'

import { IncidentDrawerContainer } from '../../components/IncidentDrawerContainer'
import {
  SOP_DRAWER_BREAKPOINT,
  SOP_DRAWER_WIDTH,
  SOP_DRAWER_WIDTH_REBRAND,
} from '../../shared/constants/drawerDimensions'
import { type IncidentContextI } from '../../shared/types/incidentContextTypes'
import { SOPIncidentDetail } from './SOPIncidentDetail'
import { SOPIncidentTable } from './SOPIncidentTable'

// Re-exported for the existing importers; shared/constants is the source.
export { SOP_DRAWER_BREAKPOINT, SOP_DRAWER_WIDTH, SOP_DRAWER_WIDTH_REBRAND }

interface SOPIncidentDrawerIProps {
  incidentContext: IncidentContextI | null
}

export const SOPIncidentDrawer = ({
  incidentContext,
}: SOPIncidentDrawerIProps) => {
  const { id, isOpen, sopNameLike } = useSelector(selectSOPIncidentDrawerState)
  const dispatch = useDispatch()
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const mode = useLightMode()
  const isDark = mode === 'dark'
  const [width] = useWindowSize()
  const drawerWidth = isRebrand ? SOP_DRAWER_WIDTH_REBRAND : SOP_DRAWER_WIDTH
  const drawerBg =
    isRebrand && isDark ? 'var(--hw-dark-colors-bg-card, #00121b)' : 'white'
  const initialPageSize = useSelector(selectSOPPageSize) ?? 25

  // Lifted from SOPsTable so changes flow through query variables and hit
  // Apollo's cache. Owning the query at the drawer level (above the table)
  // keeps the cache subscription alive when the user opens an SOP detail
  // view, which unmounts the table.
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null)

  const facilityId = incidentContext?.facility_id

  const {
    data: sopData,
    loading: isSopLoading,
    refetch: refetchSops,
  } = useStandardOperatingProceduresQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isOpen || !facilityId,
    variables: {
      filter: {
        nameLike: sopNameLike,
        type: SopType.Operator,
        facilityIdIn: facilityId ? [facilityId] : [],
      },
      first: pageIndex,
      last: pageSize,
      orderBy: getOrderBy(sortColumn, sortOrder),
    },
  })

  useEffect(() => {
    setPageIndex(0)
  }, [sopNameLike, facilityId])

  useEffect(() => {
    if (width < SOP_DRAWER_BREAKPOINT) {
      dispatch(closeSOPIncidentDrawer())
    }
  }, [width])

  if (!isOpen) return null

  return (
    <IncidentDrawerContainer
      bgColor={drawerBg}
      borderColor={
        isRebrand
          ? isDark
            ? 'var(--hw-dark-colors-border-subtle, #0c3145)'
            : 'var(--hw-dark-colors-light-border-subtle, #F1F5F9)'
          : undefined
      }
      dataTestid='SOPIncidentDrawerContainer:wrapper'
      width={drawerWidth}
    >
      {id !== null ? (
        <SOPIncidentDetail id={id} />
      ) : incidentContext ? (
        <SOPIncidentTable
          facilityId={incidentContext.facility_id}
          incidentId={incidentContext.incident_id}
          isSopLoading={isSopLoading}
          pageIndex={pageIndex}
          pageSize={pageSize}
          refetchSops={refetchSops}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          setSortColumn={setSortColumn}
          setSortOrder={setSortOrder}
          sopData={sopData}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
        />
      ) : null}
    </IncidentDrawerContainer>
  )
}
